/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ContextualHelp, Typography2 } from '@coursera/cds-core';

import { useIntl } from 'bundles/page/lib/useIntl';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  header: css`
    margin-top: var(--cds-spacing-300);
  `,
  aboutText: css`
    margin-top: var(--cds-spacing-200);
  `,
};

type Props = {
  numOfPrevDays: number;
  numOfSkills: number;
};

const TrendingSkillsHelp = ({ numOfPrevDays, numOfSkills }: Props) => {
  const intl = useIntl();

  return (
    <ContextualHelp
      css={styles.header}
      helpToggleProps={{
        'aria-label': _t('Information about trending skills'),
      }}
      tooltipProps={{
        closeButtonProps: {
          'aria-label': _t('Close information about trending skills'),
        },
      }}
      label={
        <Typography2 variant="subtitleMedium" component="h4">
          {intl.formatMessage(
            _t(
              'Top {numOfTrendingSkills} assessed {numOfTrendingSkills, plural, =0 {skills} one {skill} other {skills}} (in last {numOfPrevDays} days)'
            ),
            { numOfPrevDays, numOfTrendingSkills: numOfSkills }
          )}
        </Typography2>
      }
    >
      <Typography2 component="p">
        {_t(
          'The top 5 skills in which most learners have made progress on the platform in the selected time period. Learners make progress in a skill by completing in-course assessments or LevelSet assessments.'
        )}
      </Typography2>
    </ContextualHelp>
  );
};

const TrackedSkillsHelp = ({ numOfPrevDays, numOfSkills }: Props) => {
  const intl = useIntl();

  return (
    <ContextualHelp
      css={styles.header}
      helpToggleProps={{
        'aria-label': _t('Information about skill tracking'),
      }}
      tooltipProps={{
        closeButtonProps: {
          'aria-label': _t('Close information about skill tracking'),
        },
      }}
      label={
        <Typography2 variant="subtitleMedium" component="h4" color="supportText">
          {intl.formatMessage(
            _t(
              'Top {numOfTrackedSkills} tracked {numOfTrackedSkills, plural, =0 {Skills} one {Skill} other {Skills}}, most learners assessed (in the last {numOfPrevDays} days)'
            ),
            { numOfPrevDays, numOfTrackedSkills: numOfSkills }
          )}
        </Typography2>
      }
    >
      <React.Fragment>
        <Typography2 component="p">
          {intl.formatMessage(
            _t(
              'The top {numOfTrackedSkills} tracked {numOfTrackedSkills, plural, =0 {skills} one {skill} other {skills}} in which most learners have made progress on the platform in the selected time period. Learners make progress in a skill by completing in-course assessments or LevelSet assessments.'
            ),
            { numOfTrackedSkills: numOfSkills }
          )}
        </Typography2>
        <Typography2 component="p" css={styles.aboutText}>
          {_t('This data is updated daily.')}
        </Typography2>
      </React.Fragment>
    </ContextualHelp>
  );
};

export { TrendingSkillsHelp, TrackedSkillsHelp };
